import React, { useEffect, useRef, useState } from 'react'
import {
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { RankingInfo, rankItem } from '@tanstack/match-sorter-utils'
import { Dropdown, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import RequestFileModal from './RequestFileModal'
import { DisplayImage } from '../../../widgets/components/General/DisplayImage'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../hooks/gtmHelpers'
import '../../styles/records-and-files.scss'
import { ToolTipUI } from '../../../widgets/components/UI/ToolTipUI'
import { Link } from 'react-router-dom'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { toast } from 'react-toastify'
// import {getFileRequests} from '../../core/_requests'
import { useAuth } from '../../../auth'
import { format } from 'date-fns'
import SkeletonRow from '../../../digital-signing/components/SkeletonRow'
import { deleteFileRequest, getFileRequest, getFileRequests, getRequestFileExportCsv } from '../../core/_requests'
import { DeleteClassificationModal } from '../RecordsClassifications/DeleteClassificationModal'
import { Filter } from '../../../widgets/components/General/Filter'
import { useStakeholders } from '../../../stakeholders/Stakeholders'
import { NoDataPage } from '../../../resources/components/NoDataPage'
import clsx from 'clsx'

declare module '@tanstack/react-table' {
  interface FilterFns {
    fuzzy: FilterFn<unknown>
  }
  interface FilterMeta {
    itemRank: RankingInfo
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value)
  addMeta({ itemRank })
  return itemRank.passed
}

export const FileRequestsDataTable = () => {
  const { formatMessage } = useIntl()
  const { currentUser } = useAuth()
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [showKebabMenu, setShowKebabMenu] = useState(false)
  const [isModalActive, setIsModalActive] = useState(false)
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const handleYourRowToggle = (rowIndex: number) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex)
  }
  const [search, setSearch] = useState<any>('') //eslint-disable-line
  const [, setEditedFileId] = useState<string>('')
  const [deletedFileId, setDeletedFileId] = useState<string>('')
  const [requestedFileDataLoading, setRequestedFileDataLoading] = useState<boolean>(false)
  const [requestedFileData, setRequestedFileData] = useState<any>()
  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState<boolean>(false)
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
  const { companyId } = useAuth()
  const { setSelectedLevelFilter, selectedLevelTypeFilter, setSelectedActionFilter, selectedActionTypeFilter } =
    useStakeholders()

  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const handleClickOutside = (event: any) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setShowKebabMenu(false)
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const LinkCell = (url: string) => {
    const [hovered, setHovered] = useState(false)

    const link = url ? url
      : '-'

    const displayLink = link.length > 15 ? `${link.substring(0, 23)}...` : link

    const handleCopy = () => {
      navigator.clipboard.writeText(link)
      setHovered(true)
      toast.success('Link copied to clipboard')
      setTimeout(() => setHovered(false), 1000)
    }

    return (
      <div
        className='text-center badge text-dark fw-normal font-size-13 px-0'
        style={{ cursor: 'pointer' }}
        onClick={handleCopy}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {hovered ? (
          <span className='copy-clipboard d-flex align-items-center justify-content-center' style={{ padding: '7px 8px', height: '30px', width: '174px' }}>{formatMessage({ id: 'Copy to Clipboard' })}</span>
        ) : (
          <span style={{ background: '#4776E61A', padding: '8px 8px', borderRadius: '4px', height: '30px' }}>
            {displayLink}
          </span>
        )}
      </div>
    )
  }

  const [data, setData] = useState<any[]>([])

  const [pagination, setPagination] = useState<{
    currentPage: number
    pageSize: number
    totalCount: number
    nextPage: number
    prevPage: number | null
    totalPages: number
  }>({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    nextPage: 0,
    prevPage: null,
    totalPages: 0,
  })

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'requestName',
        header: 'Request Name',
        cell: (info) => info.row.original.getActivity.name,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'link',
        id: 'link',
        header: 'Request Link',
        cell: (info) => { return LinkCell(info.row.original.link) },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'urgencyLevel',
        id: 'urgency',
        header: 'Urgency',
        cell: (info) => info.row.original.getActivity.urgencyLevel,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'action',
        id: 'status',
        header: 'Status',
        cell: (info) => {
          const status = info.row.original.getActivity.action // Assuming this is a single role or an array
          console.log(status);
          return (
            <div
              className={`badge py-2 px-6 ${status === "REJECTED" ? 'alert-danger' : status === 'VIEWED' ? 'alert-primary' : status === 'UPLOADED' ? 'alert-success' : 'alert-warning'}`}
            >
              <span className="flex-root">{status}</span>
            </div>
          )
        },
        filterFn: 'includesString',
      },
      {
        accessorKey: 'recordKeeperFiles',
        id: 'filesUploaded',
        header: 'Files Uploaded',
        cell: (info) => info.row.original.files,
        filterFn: 'includesString',
      },
      {
        accessorKey: 'createdAt',
        id: 'accessTime',
        header: 'Generated Date & Time',
        cell: (info) => {
          const rawDate = info.row.original.getActivity.createdAt
          const formattedDate = rawDate ? format(new Date(rawDate), 'dd MMM, yyyy hh:mm a') : '-'
          return <span>{formattedDate}</span>
        },
        filterFn: 'includesString',
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    // Function to handle clicks outside of the dropdown menu
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowKebabMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'fuzzy',
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const pageCount = Math.ceil(table.getPrePaginationRowModel().rows.length / pagination.pageSize)
  const currentPage = table.getState().pagination.pageIndex

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({ ...prev, currentPage: pageNumber }))
  }

  function handleCopy(link: string, event: React.MouseEvent<HTMLImageElement>) {
    if (!link) {
      toast.error('No link to copy')
      return
    }

    navigator.clipboard
      .writeText(link)
      .then(() => {
        toast.success('Text copied to clipboard')
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.ShareSocialMedia,
            action: 'normal',
            metadata: {
              link, // Dynamically copied link
              company_id: 'static_company_id', // Static data
              user_id: 'static_user_id', // Static data
              email: 'static_email@example.com', // Static data
            },
          },
        })
      })
      .catch((err) => {
        console.error('Failed to copy: ', err)
        toast.error('Failed to copy link')
      })

    event.preventDefault()
  }

  const getAllFileRequest = async () => {
    setIsLoading(true)
    if (!companyId) return
    try {
      const {
        data: { success, data },
      } = await getFileRequests(
        companyId,
        pagination.currentPage,
        pagination.pageSize,
        search,
        selectedLevelTypeFilter,
        selectedActionTypeFilter
      )
      if (success) {
        setData(data?.data)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getAllFileRequest()
  }, [companyId, pagination.currentPage, pagination.pageSize, search, selectedLevelTypeFilter, selectedActionTypeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const getRequestedFileData = async (requestedFileId: string) => {
    if (!companyId) return
    setRequestedFileDataLoading(true)
    try {
      const {
        data: { success, data },
      } = await getFileRequest(companyId, requestedFileId)
      if (success) {
        setRequestedFileData(data)
      }
    } catch (err) {
      console.log(err)
    } finally {
      setRequestedFileDataLoading(false)
    }
  }

  const handleDeleteClassification = async () => {
    if (!companyId) return
    try {
      setDeleteLoading(true)
      const {
        data: { success, errors },
      } = await deleteFileRequest(companyId, deletedFileId)
      if (success) {
        toast.success(`File request deleted successfully`)
        getAllFileRequest()
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
      console.log(err)
    } finally {
      setDeleteLoading(false)
      setOpenDeleteConfirmationModal(false)
    }
  }
  const exportToCsv = async () => {
    if (!companyId) return
    try {
      const {
        data: { data: values, success, errors },
      } = await getRequestFileExportCsv(companyId)
      if (success) {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.MeetingExportCsvSuccessful,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email
            },
          },
        })
        toast.success(formatMessage({ id: 'Documents exported successfully' }))
        const url = window.URL.createObjectURL(new Blob([values]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'request-file-details.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
      } else {
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.MeetingExportCsvFailed,
            action: 'normal',
            metadata: {
              company_id: companyId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              error: errors[0]
            },
          },
        })
        errors.forEach((error: string) => {
          toast.error(formatMessage({ id: error }))
        })
      }
    } catch (err: any) {
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.MeetingExportCsvFailed,
          action: 'normal',
          metadata: {
            company_id: companyId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            error: err
          },
        },
      })
    }
  }
  return (
    <>
      <div className='d-flex flex-md-row flex-column align-items-md-center gap-2 mb-4'>
        <div className='d-flex justify-content-between w-100 gap-1 height-36'>
          <div className='h-100 bg-white d-flex rounded'>
            <button
              className={clsx(
                'text-clr88 btn btn-sm btn-flex fw-semibold py-2 font-size-12 height-36'
              )}
              id='fc-toogle'
              title='Sort and Filter'
              data-bs-toggle='tooltip'
              data-bs-placement='left'
              data-bs-dismiss='click'
              data-bs-trigger='hover'
              onClick={() => {
                setSelectedLevelFilter(true)
                setSelectedActionFilter(true)
              }}
            >
              <KTSVG
                path={toAbsoluteUrl('/media/icons/duotune/general/gen031.svg')}
                className='svg-icon-6 svg-icon-muted me-1'
              />
              <span className='d-none d-md-block'>{formatMessage({ id: 'Filter' })}</span>
            </button>
          </div>
          <form className=' align-items-center w-100 w-md-auto d-flex height-36'>
            <span className='svg-icon svg-icon-3 position-absolute ms-3'>
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className='mh-50px'
              >
                <rect
                  opacity='0.5'
                  x='17.0365'
                  y='15.1223'
                  width='8.15546'
                  height='2'
                  rx='1'
                  transform='rotate(45 17.0365 15.1223)'
                  fill='currentColor'
                ></rect>
                <path
                  d='M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z'
                  fill='currentColor'
                ></path>
              </svg>
            </span>
            <DebouncedInput
              value={search ?? ''}
              onChange={(value) => {
                // setGlobalFilter(String(value)
                setSearch(String(value))
                if (String(value).length > 0) {
                  TagManager.dataLayer({
                    dataLayer: {
                      event: GTMEvent.AdvisorMentorSearchSuccessful,
                      action: 'normal',
                      // metadata: {
                      //     company_id: companyId,
                      //     user_id: currentUser?.userId,
                      //     email: currentUser?.email,
                      //     search_value: value,
                      // },
                    },
                  })
                }
              }}
              className='bg-body form-control form-control-flush ps-10 rounded-2 font-size-13 font-weight-400 height-36'
              placeholder='Search'
            />
          </form>
        </div>
        <div className='d-flex gap-1 flex-shrink-0'>
          <button
            className='btn text-white custom-button-property bg-primary height-36 d-flex align-items-center font-size-12 w-100 justify-content-center '
            onClick={() => {
              setIsModalActive(true)
              setRequestedFileData('')
            }
            }
          >
            {formatMessage({ id: 'Request A File' })}
          </button>
          <RequestFileModal
            isModalActive={isModalActive}
            setIsModalActive={setIsModalActive}
            getAllFileRequest={getAllFileRequest}
            requestedFileData={requestedFileData}
            requestedFileDataLoading={requestedFileDataLoading}
          />
          <div className='position-relative align-self-top height-36'>
            <div
              className='p-3 cursor-pointer bg-white rounded-2 d-flex justify-content-center align-items-center height-36'
              onClick={() => {
                setShowKebabMenu(!showKebabMenu)
              }}
            >
              <img src={threeDots} alt='' width={16} height={16} />
            </div>
            {showKebabMenu && (
              <div
                className='card shadow rounded top-4 cursor-pointer z-index-1 position-absolute w-150px'
                style={{ top: '40px', right: 0 }}
                ref={dropdownRef}
              >
                <div className='border-bottom border-gray-300 border-bottom-2 p-2 dropdown-items w-100 bg-hover-light'>
                  <button
                    type='button'
                    className='m-0 border-0 bg-transparent text-dark font-size-12 bg-hover-muted'
                    onClick={exportToCsv}
                  >
                    {formatMessage({ id: 'Export to CSV' })}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='bg-white rounded-top p-4' style={{ overflowX: 'auto' }}>
        {!isLoading && (data?.length === 0 || table.getRowModel().rows.length === 0) ? (
          <div className='my-5 mx-6'>
            <NoDataPage />
          </div>
        ) : (
          <Table className='w-100' style={{ minWidth: '600px', cursor: 'pointer' }}>
            <thead className='custom-thead border-bottom'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr
                  key={headerGroup.id}
                  className='font-size-12 font-weight-400 table-row border-dark-mode'
                >
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className='min-w-120px pb-6 pt-4 px-2'
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      )}
                    </th>
                  ))}
                  <th className='min-w-120px pb-6 pt-4'>{formatMessage({ id: 'Actions' })}</th>
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow count={8} />
                </>
              ) : (
                table.getRowModel().rows.map((row, index) => (
                  <React.Fragment key={row.id}>
                    <tr
                      key={row.id}
                      onClick={() => handleYourRowToggle(index)}
                      className={`border-bottom ${expandedRow === index ? 'expanded-row-captable bg-light' : 'bg-white'
                        }`}
                    >
                      {row.getVisibleCells().map((cell, index) => (
                        index === 0 ?
                          <>
                            <td key={cell.id} className='pt-5 border-0 px-2' style={{ minWidth: '250px', maxWidth: '300px' }}>
                              <div className='text-wrap'>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                            </td>
                          </> :
                          index === 1 ?
                            <>
                              <td key={cell.id} className='pt-3 border-0 px-2' style={{ minWidth: '120px', maxWidth: '300px' }}>
                                <div className='text-wrap'
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                              </td>
                            </> :
                            index === 4 ?
                              <>
                                <td key={cell.id} className='pt-6 border-0 px-2' style={{ minWidth: '120px', maxWidth: '300px' }}>
                                  <div className='text-wrap'>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                </td>
                              </>
                              :
                              <>
                                <td key={cell.id} className='pt-5 border-0 px-2' style={{ minWidth: '120px', maxWidth: '300px' }}>
                                  <div className='text-wrap'>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                </td>
                              </>
                      ))}
                      <td className='pt-5' onClick={(e) => e.stopPropagation()}>
                        <div className='dropdown-container d-flex justify-content-center align-items-center pb-1'>
                          <Dropdown
                            show={index === selectedIndex}
                            onToggle={(isOpen, event) => {
                              if (event.source === 'click') {
                                setSelectedIndex(isOpen ? index : undefined)
                              } else {
                                setSelectedIndex(undefined)
                              }
                            }}
                          >
                            <Dropdown.Toggle className='btn btn-white table-three-dots dots-container'>
                              <img src={threeDots} alt='' className='three-dots-image' />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className='m-2'>
                              {row?.original?.getActivity?.action === 'REQUESTED' && <Dropdown.Item
                                className='text-dark ps-5'
                                onClick={() => {
                                  setEditedFileId(row.original?.getActivity?.requestFileId)
                                  getRequestedFileData(row.original?.getActivity?.requestFileId)
                                  setIsModalActive(true)

                                }}
                              >
                                {formatMessage({ id: 'Edit' })}
                              </Dropdown.Item>}
                              <Dropdown.Item
                                className='text-danger ps-5'
                                onClick={() => {
                                  setOpenDeleteConfirmationModal(true)
                                  setDeletedFileId(row.original?.getActivity?.requestFileId)
                                }}
                              >
                                <p className='mb-0'>{formatMessage({ id: 'Delete' })}</p>
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === index && (
                      <tr className='expanded-row-captable bg-light'>
                        <td colSpan={1} className='min-w-120px pb-6 pt-4 px-3' style={{ minWidth: '220px', maxWidth: '250px' }}>
                          <div>
                            <p className='ps-2'>{formatMessage({ id: 'Requested to' })}</p>
                            <div className='ps-4'>
                              {row.original.getActivity.emailTo?.split(',').map((email: string, idx: any) => (
                                <div
                                  key={idx}
                                  className='d-flex align-items-center p-2 bg-secondary rounded gap-1 my-2 w-100'
                                  style={{ width: 'max-content' }}
                                >
                                  <DisplayImage
                                    className='rounded-circle'
                                    imgName=''
                                    alt='profile-pic'
                                    height={20}
                                    width={20}
                                  />
                                  <p className='m-0 text-dark fw-normal'>{email.trim()}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className='mt-8 ps-4'>
                            <div className='d-flex align-items-center'>
                              <p className='m-0'>
                                {formatMessage({ id: 'Request Link to upload Files' })}
                              </p>
                              <ToolTipUI tooltipText='The country that the document is associated with, either from the content or from its purpose (e.g., taxes or compliance).' />
                            </div>
                            <div className='d-flex align-items-center mt-2'>
                              <Link to='#'>
                                {row.original.link}
                              </Link>

                              <img
                                src={toAbsoluteUrl('/media/icons/RecordsKeeperAI/ic-copy.svg')}
                                alt='copy icon'
                                className='ms-2 cursor-pointer'
                                onClick={(event) =>
                                  handleCopy(
                                    `${row.original.link}`,
                                    event
                                  )
                                }
                              />
                            </div>
                          </div>
                        </td>
                        <td colSpan={6} className='min-w-120px pb-6 pt-4 text-wrap' style={{ maxWidth: '300px' }}>
                          <div className='w-100'>
                            <p>{formatMessage({ id: 'Message' })}</p>
                            <div className='px-4'>
                              <p className='text-wrap' >{row?.original?.getActivity?.message?.replace(/<[^>]+>/g, '')}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      <div className='w-100 d-md-flex justify-content-between align-items-center px-4 py-4 bg-white rounded-bottom mb-8'>
        <div className='d-flex gap-2 w-100 align-items-center justify-content-md-start justify-content-center mb-4 mb-md-0'>
          <select
            aria-label='Default select example'
            className='p-4 py-3 w-80px border border-1 rounded-2 font-size-13 bg-white text-dark '
            style={{ outline: 'none' }}
            value={pagination.pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value)
              setPagination((prev) => ({
                ...prev,
                pageSize: newSize,
                pageIndex: 0, // Reset to first page when page size changes
              }))
            }}
          >
            {[10, 20, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>

          <span className='font-size-13 text-center text-md-start'>
            {formatMessage({
              id: `Showing ${currentPage * pagination.pageSize + 1} to ${Math.min(
                (currentPage + 1) * pagination.pageSize,
                table.getPrePaginationRowModel().rows.length
              )} of ${table.getPrePaginationRowModel().rows.length} records`,
            })}
          </span>
        </div>

        {/* <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {Array.from({ length: pageCount }, (_, i) => (
            <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
              {i + 1}
            </Pagination.Item>
          ))}

          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination> */}
        <Pagination className='w-md-100 w-auto d-md-flex flex-md-end'>
          <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0}>
            {'<<'}
          </Pagination.First>

          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {'<'}
          </Pagination.Prev>

          {(() => {
            const items = [];
            const totalPages = pageCount;
            const pageNeighbours = 1; // Show 1 page before and after current
            const startPage = Math.max(1, currentPage - pageNeighbours);
            const endPage = Math.min(totalPages - 2, currentPage + pageNeighbours);

            // Always show the first page
            items.push(
              <Pagination.Item
                key={0}
                active={currentPage === 0}
                onClick={() => handlePageChange(0)}
              >
                1
              </Pagination.Item>
            );

            // Start ellipsis
            if (startPage > 1) {
              items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }

            // Middle pages
            for (let i = startPage; i <= endPage; i++) {
              items.push(
                <Pagination.Item
                  key={i}
                  active={currentPage === i}
                  onClick={() => handlePageChange(i)}
                >
                  {i + 1}
                </Pagination.Item>
              );
            }

            // End ellipsis
            if (endPage < totalPages - 2) {
              items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }

            // Last page (if more than 1 page)
            if (totalPages > 1) {
              items.push(
                <Pagination.Item
                  key={totalPages - 1}
                  active={currentPage === totalPages - 1}
                  onClick={() => handlePageChange(totalPages - 1)}
                >
                  {totalPages}
                </Pagination.Item>
              );
            }

            return items;
          })()}

          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>'}
          </Pagination.Next>

          <Pagination.Last
            onClick={() => handlePageChange(pageCount - 1)}
            disabled={currentPage >= pageCount - 1}
          >
            {'>>'}
          </Pagination.Last>
        </Pagination>

      </div>
      <DeleteClassificationModal
        setDeleteShowModal={setOpenDeleteConfirmationModal}
        showDeleteModal={openDeleteConfirmationModal}
        handleDelete={handleDeleteClassification}
        deleteLoading={deleteLoading}
        headingMessage={`Delete file request`}
        description={`Are you sure you want to delete this file request?`}
      />
      <Filter />
    </>
  )
}

function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value, debounce, onChange])
  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} />
}
